import { useEffect, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { doc, getDoc } from 'firebase/firestore';
import {
  BANK_TRANSFER_FIELDS,
  BANK_TRANSFER_MAX,
  resolveBankTransfer,
  type BankTransferDetails,
} from '@dosfilos/domain';
import { db } from '../../../lib/firebase';
import { tenantConfigApi } from '../../../lib/api/tenant-config.api';
import { errorText } from '../../../lib/error-text';

// ─── Pagos por transferencia: los datos de ESTE seminario (SEC-3) ──────────
// Los ve el alumno en su portal (Pagos → «Cómo pagar»), y solo los de su
// seminario. Los guarda `updateTenantConfig` (administración del seminario),
// validados y auditados. Se guardan siempre como textos: vaciarlos es la forma
// de dejar de mostrarlos (sin titular, banco y número de cuenta, no se muestra).

const EMPTY: BankTransferDetails = {
  holder: '',
  taxId: '',
  bank: '',
  accountType: '',
  accountNumber: '',
  email: '',
};

export function BankTransferEditor({ tenantId }: { tenantId: string }): JSX.Element {
  const { t } = useTranslation('sis');
  const k = 'settings.bankTransfer';
  const queryClient = useQueryClient();
  const saved = useQuery({
    queryKey: ['tenant-bank-transfer', tenantId],
    queryFn: async () => {
      const snap = await getDoc(doc(db, `tenants/${tenantId}`));
      const raw = (snap.data()?.['config']?.['bankTransfer'] ?? {}) as Partial<BankTransferDetails>;
      return { ...EMPTY, ...raw } as BankTransferDetails;
    },
  });
  const [draft, setDraft] = useState<BankTransferDetails>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => {
    if (saved.data) setDraft(saved.data);
  }, [saved.data]);

  const changed = saved.data && BANK_TRANSFER_FIELDS.some((f) => draft[f] !== saved.data![f]);
  const shown = resolveBankTransfer({ bankTransfer: draft }) !== null;

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await tenantConfigApi.updateTenantConfig({ tenantId, bankTransfer: draft });
      await queryClient.invalidateQueries({ queryKey: ['tenant-bank-transfer', tenantId] });
      await queryClient.invalidateQueries({ queryKey: ['payment-instructions', tenantId] });
      setMsg({ ok: true, text: t(`${k}.saved`) });
    } catch (e) {
      setMsg({ ok: false, text: errorText(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700}>
        {t(`${k}.title`)}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
        {t(`${k}.desc`)}
      </Typography>
      <Stack spacing={1.5} sx={{ maxWidth: 520 }}>
        {BANK_TRANSFER_FIELDS.map((f) => (
          <TextField
            key={f}
            size="small"
            label={t(`tuition.paymentInfo.fields.${f}`)}
            value={draft[f]}
            onChange={(e) => setDraft((d) => ({ ...d, [f]: e.target.value }))}
            inputProps={{ maxLength: BANK_TRANSFER_MAX }}
          />
        ))}
        <Typography variant="caption" color={shown ? 'text.secondary' : 'warning.main'}>
          {shown ? t(`${k}.shown`) : t(`${k}.hidden`)}
        </Typography>
        <Box>
          <Button variant="outlined" onClick={() => void save()} disabled={busy || !changed}>
            {t(`${k}.save`)}
          </Button>
        </Box>
        {msg && <Alert severity={msg.ok ? 'success' : 'error'}>{msg.text}</Alert>}
      </Stack>
    </Box>
  );
}
